/**
 * FCM token register/deregister transport.
 *
 * `rest/notifications/fcm/` and `rest/notifications/fcm/deregister` are not
 * in the OpenAPI schema (same as AIM — see
 * `aegix-aim-react/src/main/services/fcm/index.tsx`), so both calls go
 * through the app's own authenticated ky transport, injected here. That
 * keeps each app's real middleware in the loop — 401-queue refresh, header
 * rotation, vendor-org middleware — instead of hand-rolling cookie/header
 * handling.
 *
 * Registration is a POST (`RegisterFCM` is a CreateAPIView); deregistration
 * is a **PUT** (`DeregisterFCM` is an `UpdateAPIView` addressing the
 * `FCMDeviceToken` row by token string in the body —
 * `server/apps/notifications/views.py`; the backend's own tests call it
 * with PUT). Both carry `x-device-id` so the backend can sweep this
 * device's stale registrations.
 */

export const FCM_REGISTER_PATH = 'rest/notifications/fcm/';
export const FCM_DEREGISTER_PATH = 'rest/notifications/fcm/deregister';

/** Registration payload: AIM's `FCMType` fields, all required, plus `app_identifier`. */
export interface WebPushRegistrationPayload {
  token: string;
  platform: string;
  /** Product routing axis — backend `fcm_routing` set membership. */
  app_name: string;
  /** Env-major Firebase Web App entry display name (ADR-132 A4 §3). */
  app_identifier: string;
  app_version: string;
  device_id: string;
  device_model: string;
  os_version: string;
  locale: string;
  timezone: string;
  screen_resolution: string;
}

export interface WebPushRegistrationResponse {
  token: string;
}

/** The awaited shape of a transport response (ky's `KyResponse` satisfies it). */
export interface WebPushTransportResponse {
  json(): Promise<unknown>;
}

/**
 * Structural slice of the app's authenticated ky instance. Apps inject
 * `client.ky` — the lib never owns cookies, refresh queues, or base URLs.
 */
export interface WebPushTransport {
  post(
    url: string,
    options: { headers?: Record<string, string>; json?: unknown },
  ): PromiseLike<WebPushTransportResponse>;
  put(
    url: string,
    options: { headers?: Record<string, string>; json?: unknown },
  ): PromiseLike<unknown>;
}

export interface FcmApi {
  /**
   * POST the registration. Returns the raw transport response — the caller
   * decides whether to read the body (SMS parses `{ token }`, ONE ignores
   * it), keeping each app's existing behavior intact.
   */
  register(
    payload: WebPushRegistrationPayload,
  ): Promise<WebPushTransportResponse>;
  /**
   * PUT `rest/notifications/fcm/deregister` for the given token. The
   * `x-device-id` header is omitted when `deviceId` is null/empty —
   * deregistration must never invent an identity.
   */
  deregister(token: string, deviceId: string | null): Promise<void>;
}

export function createFcmApi(transport: WebPushTransport): FcmApi {
  return {
    async register(payload) {
      return await transport.post(FCM_REGISTER_PATH, {
        headers: { 'x-device-id': payload.device_id },
        json: payload,
      });
    },
    async deregister(token, deviceId) {
      const headers: Record<string, string> = {};
      if (deviceId) headers['x-device-id'] = deviceId;
      await transport.put(FCM_DEREGISTER_PATH, {
        headers,
        json: { token },
      });
    },
  };
}
